import { HttpEvent, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { MicroApiService } from '../constants/micro-api.service';

// Xano's standard file-resource shape — the uploaded attachment itself.
export interface UploadedModel {
  access?: string;
  path?: string;
  name?: string;
  type?: string;
  size?: number;
  mime?: string;
  meta?: Record<string, any>;
  url?: string;
}

// The saved record returned by /upload/model and listed by /my_models — it wraps the file.
export interface ModelRecord {
  id?: number | string; // a uuid on the gateway
  created_at?: number | string;
  user_id?: number | string;
  created_by?: number | string;
  name?: string;
  model?: UploadedModel;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class Model3dService {
  // The business's own model library on the gateway: UPLOAD_MODEL (2459) and MY_MODELS (2460).
  // The same rows the admin portal's 3D Models page (8262-8265) manages, scoped to the signed-in
  // business by the server.
  private micro = inject(MicroApiService);

  /**
   * The gateway is JSON-only, so the .glb travels as a base64 data URI in `file` (the same way
   * the dashboard sends every image). The component was written for Xano's multipart upload and
   * waits for an HttpEventType.Response event, so the JSON reply is handed back as exactly that.
   */
  uploadModel(file: File, name = ''): Observable<HttpEvent<ModelRecord>> {
    return from(this.readAsDataUrl(file)).pipe(
      switchMap((dataUrl) => this.micro.route('UPLOAD_MODEL', false, { name, file_name: file.name, file: dataUrl })),
      map((body: ModelRecord) => new HttpResponse<ModelRecord>({ body, status: 200 }) as HttpEvent<ModelRecord>),
    );
  }

  private readAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject({ error: { message: 'The file could not be read.' }, status: 0 });
      reader.readAsDataURL(file);
    });
  }

  getMyModels(): Observable<any> {
    return this.micro.route('MY_MODELS', true);
  }

  deleteModel(id: number | string): Observable<any> {
    return this.micro.route('DELETE_MODEL', false, { id });
  }

  /* ------------------------------------------------------------------ */
  /*                     Response shape helpers                         */
  /* ------------------------------------------------------------------ */

  // /my_models returns either a bare array or a paginated envelope.
  toRecordList(res: any): ModelRecord[] {
    return Array.isArray(res) ? res : res?.items || res?.data || [];
  }

  // A record wraps the file resource (e.g. { id, name, model: {...} }), so name/size/mime
  // have to be read from the nested object, not the root.
  getFileObject(source: any, depth = 0): UploadedModel | null {
    if (!source || typeof source !== 'object' || depth > 4) return null;

    if (Array.isArray(source)) {
      for (const entry of source) {
        const found = this.getFileObject(entry, depth + 1);
        if (found) return found;
      }
      return null;
    }

    if (typeof source['path'] === 'string' && source['path']) {
      return source as UploadedModel;
    }

    for (const value of Object.values(source)) {
      const found = this.getFileObject(value, depth + 1);
      if (found) return found;
    }

    return null;
  }

  // Only an absolute `url` is a usable link. A bare vault `path` belonged to the removed Xano
  // file store, so it resolves to nothing rather than to that host.
  getModelUrl(source: any): string {
    const candidate = this.findUrlCandidate(source);
    return /^https?:\/\//i.test(candidate) ? candidate : '';
  }

  private findUrlCandidate(source: any, depth = 0): string {
    if (!source || depth > 4) return '';

    if (typeof source === 'string') {
      // Only strings that actually look like a link — not names, mime types, etc.
      return /^https?:\/\//i.test(source) || source.startsWith('/') ? source : '';
    }

    if (Array.isArray(source)) {
      for (const entry of source) {
        const found = this.findUrlCandidate(entry, depth + 1);
        if (found) return found;
      }
      return '';
    }

    if (typeof source === 'object') {
      for (const key of ['url', 'path']) {
        const value = source[key];
        if (typeof value === 'string' && value) return value;
      }
      for (const value of Object.values(source)) {
        const found = this.findUrlCandidate(value, depth + 1);
        if (found) return found;
      }
    }

    return '';
  }
}
